import {Component, Input, OnDestroy, OnInit, Pipe, PipeTransform, TemplateRef, ViewChild} from '@angular/core';
import {NavigationEnd, Router} from "@angular/router";
import {filter} from "rxjs/operators";
import {Subscription} from "rxjs";
import {environment} from 'src/environments/environment';
import {appEnv, isProduction} from "../../utils/app_env";
import {User, UserPermissionsService} from "../../services/user-permissions.service";
import {ThemeModeService} from "../../_metronic/partials/layout/theme-mode-switcher/theme-mode.service";
import {BackendApiService} from "../../services/backend-api.service";
import {VersionStreamService} from "../../services/version-stream.service";
import {UsageTrackerService} from "../../services/usage-tracker.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {getEnvBadgeClass, getUserInitials} from "../../utils/helpers";
import {confirmDialog} from "../../utils/sweetalert";

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, OnDestroy {
    @Input() environment: string;
    @Input() theme: any;
    @Input() requestUri: string;

    protected readonly appEnv = appEnv;
    protected readonly getUserInitials = getUserInitials;

    searching: boolean = false;
    current = {global_search: ''};
    notifications: any;
    user: User;
    profilePicBroken: boolean = false;

    @ViewChild('sessionExpiredModal') sessionExpiredModal: TemplateRef<any>;

    appInfo: any = {};
    // Persists across a toast dismissal — drives the navbar's own "1.0.0 → 1.0.1" indicator,
    // which should keep showing even after the toast itself is closed.
    latestVersionInfo: {version: string; versionUrl: string; environment: string} | null = null;
    dismissedVersion: string | null = localStorage.getItem('dismissed_version');
    // Only shown once the user cancels the confirmDialog popup for this version — not
    // alongside it — so the toast acts as the popup's persistent follow-up, not a duplicate.
    versionToastVisible: boolean = false;
    private authDialogShowing: boolean = false;
    private versionDialogShowing: boolean = false;
    private authChannel = new BroadcastChannel('auth');
    private versionStreamSubscriptions = new Subscription();


    // Admin → Usage header: shown when the user can open at least one of its pages (General or API)
    get canSeeUsage(): boolean {
        return this.userPermissionsService.hasComponentPermission('can_read_hexagon_usage')
            || this.userPermissionsService.hasComponentPermission('can_read_hexagon_usage_api');
    }

    constructor(
        private router: Router,
        private backendApiService: BackendApiService,
        private userPermissionsService: UserPermissionsService,
        private modeService: ThemeModeService,
        private modalService: NgbModal,
        private versionStreamService: VersionStreamService,
        private usageTracker: UsageTrackerService,
    ) {
        // Set the default environment here
        this.environment = environment.ENVIRONMENT || 'development';
        this.notifications = {};
    }

    ngOnInit() {
        this.userPermissionsService.getUserDetails().then((user: User) => {
            this.user = user;
        });
        this.getAppInfo();
        this.checkForTheNewVersion();

        this.versionStreamSubscriptions.add(
            this.versionStreamService.events$.subscribe(event => {
                if (this.latestVersionInfo?.version === event.version) return;
                if (this.appInfo?.version && this.appInfo.version === event.version) return;
                this.latestVersionInfo = {version: event.version, versionUrl: event.version_url, environment: event.environment};
                this.promptForNewVersion();
            })
        );
        this.versionStreamSubscriptions.add(
            this.versionStreamService.reconnected$.subscribe(() => this.checkForVersionMismatch())
        );

        this.authChannel.onmessage = (event) => {
            if (event.data?.type === 'logout') {
                this.showAuthDialog();
            }
        };

        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.closeMobileNavOffcanvas());

        this.initOffcanvasContentAnimation();
    }

    private closeMobileNavOffcanvas(): void {
        const offcanvasElement = document.getElementById('navbarOffcanvas');
        if (!offcanvasElement) return;
        (window as any).bootstrap?.Offcanvas.getInstance(offcanvasElement)?.hide();
    }

    // CSS animations take priority over CSS transitions on the same property while they're
    // running, so this plays on top of (and visually dominates) Bootstrap's own transform
    // transition without needing to disable it — leaving Bootstrap's own transitionend-based
    // show/hidden timing intact.
    private initOffcanvasContentAnimation(): void {
        const offcanvasElement = document.getElementById('navbarOffcanvas');
        if (!offcanvasElement) return;

        offcanvasElement.style.setProperty('--animate-duration', '0.25s');

        offcanvasElement.addEventListener('show.bs.offcanvas', () => {
            offcanvasElement.classList.remove('animate__slideOutRight');
            offcanvasElement.classList.add('animate__animated', 'animate__slideInRight');
        });
        offcanvasElement.addEventListener('hide.bs.offcanvas', () => {
            offcanvasElement.classList.remove('animate__slideInRight');
            offcanvasElement.classList.add('animate__animated', 'animate__slideOutRight');
        });
        offcanvasElement.addEventListener('hidden.bs.offcanvas', () => {
            offcanvasElement.classList.remove('animate__animated', 'animate__slideInRight', 'animate__slideOutRight');
        });
    }

    ngOnDestroy() {
        this.authChannel.close();
        this.versionStreamSubscriptions.unsubscribe();
    }

    refreshForNewVersion(): void {
        window.location.reload();
    }

    onVersionBadgeClick(event: MouseEvent): void {
        if (!this.latestVersionInfo) return;
        event.preventDefault();
        this.refreshForNewVersion();
    }

    dismissNewVersionBanner(): void {
        if (!this.latestVersionInfo) return;
        this.dismissedVersion = this.latestVersionInfo.version;
        localStorage.setItem('dismissed_version', this.dismissedVersion);
    }

    private promptForNewVersion(): void {
        if (!this.latestVersionInfo || this.versionDialogShowing) return;
        if (this.latestVersionInfo.version === this.dismissedVersion) return;
        this.versionDialogShowing = true;
        const envBadgeClass = getEnvBadgeClass(this.latestVersionInfo.environment);
        confirmDialog({
            title: 'New version available',
            description: `Refresh the page to get the latest version.<br>
                <a href="${this.latestVersionInfo.versionUrl}" target="_blank" class="badge env-badge ${envBadgeClass} mt-2">${this.latestVersionInfo.version}</a>`,
            confirmButtonText: 'Refresh',
            cancelButtonText: 'Cancel',
            showLogo: true,
        }).then(confirmed => {
            this.versionDialogShowing = false;
            if (confirmed) {
                this.refreshForNewVersion();
            } else {
                this.versionToastVisible = true;
            }
        });
    }

    search(value: string) {
        // Implement search logic here.
    }

    isProduction() {
        return isProduction();
    }

    onSelect(selectedItem: any) {
        // Implement the action to perform when an item is selected.
    }

    reportIssue(data: any) {
        const userEmail = this.user.email;

        const gl_report_script = document.createElement('script');
        gl_report_script.async = true;
        gl_report_script.src = 'https://report-gamelounge-com-oeh6pjyu7a-nw.a.run.app/assets/js/widget.js';
        var x = document.getElementsByTagName('script')[0];
        x.parentNode?.insertBefore(gl_report_script, x);

        setTimeout(function () {
            document.getElementById('glEr-badge')?.dispatchEvent(new MouseEvent('click', {view: window}));

            setTimeout(function () {
                if (data.title) {
                    let titleEl: any = document.querySelector('#glEr-elms #title');
                    if (titleEl) titleEl.value = data.title;
                }
                if (data.description) {
                    let descEl: any = document.querySelector('#glEr-elms #description');
                    if (descEl) descEl.value = data.description;
                }
                let reporterEl: any = document.querySelector('#glEr-elms #reporter');
                if (reporterEl) reporterEl.value = userEmail;
            }, 300);
        }, 1600);
    }

    switchThemeMode() {
        if (this.modeService.mode.value == 'light') {
            this.modeService.updateMode('dark');
        } else {
            this.modeService.updateMode('light');
        }
    }

    isGamesActive(): boolean {
        const url = this.router.url.split('?')[0];
        const gameSubPages = ['/games/types', '/games/manufacturers', '/games/features', '/games/themes', '/games/upcoming', '/games/market-preferred-providers'];
        return url.startsWith('/games') && !gameSubPages.some(p => url.startsWith(p));
    }

    baseUrl() {
        return window.location.protocol + "//" + window.location.hostname
    }

    handleImageError() {
        this.profilePicBroken = true;
    }

    hasProfilePic(): boolean {
        const pic = this.user?.['pic-internal'];
        return !!pic && !pic.includes('empty_author') && !this.profilePicBroken;
    }

    getThemeMode() {
        return this.modeService.mode.value;
    }

    logout() {
        this.usageTracker.flush();  // send the last usage stats while the session is still valid (never throws)
        this.backendApiService.post('/auth/logout', {})
            .subscribe(() => {
                this.authChannel.postMessage({ type: 'logout' });
                location.href = '/new_cdb/auth/login';
            });
    }

    getAppInfo(): void {
        const thisComponent = this;

        this.backendApiService.get('/status')
            .subscribe({
                next(res: any) {
                    thisComponent.appInfo = res.data;
                    // Self-heal a race where the version_changed SSE event arrived before this
                    // /status response did — the events$ handler's "already have this version"
                    // guard would have been bypassed since appInfo.version wasn't loaded yet.
                    if (thisComponent.latestVersionInfo?.version === res.data.version) {
                        thisComponent.latestVersionInfo = null;
                    }
                    if (!res.data.authenticated) {
                        thisComponent.showAuthDialog();
                    }
                },
                error(err) {
                    console.error(err);
                    thisComponent.appInfo = {};
                }
            });
    }

    // Used after an SSE reconnect to check whether we missed a version_changed event while
    // disconnected — deliberately does NOT touch this.appInfo, which is the frozen baseline
    // of what version this browser tab is actually running. Reassigning it to the server's
    // current version here would make the badge/toast think the tab is already up to date
    // even though nothing has actually reloaded.
    private checkForVersionMismatch(): void {
        this.backendApiService.get('/status').subscribe({
            next: (res: any) => {
                if (this.appInfo?.version && res.data.version && this.appInfo.version !== res.data.version) {
                    if (this.latestVersionInfo?.version !== res.data.version) {
                        this.latestVersionInfo = {version: res.data.version, versionUrl: res.data.version_url, environment: res.data.environment};
                        this.promptForNewVersion();
                    }
                } else if (this.latestVersionInfo && this.appInfo?.version === res.data.version) {
                    this.latestVersionInfo = null;
                }
            },
            error: () => {}
        });
    }

    onSessionRestored(): void {
        this.modalService.dismissAll();
        this.authDialogShowing = false;
    }

    private showAuthDialog(): void {
        if (this.authDialogShowing) return;
        this.authDialogShowing = true;
        document.body.classList.add('session-expired-active');
        const modalRef = this.modalService.open(this.sessionExpiredModal, { backdrop: 'static', keyboard: false, centered: true, windowClass: 'session-expired-modal-window' });
        modalRef.result.finally(() => document.body.classList.remove('session-expired-active'));
    }

    // Version-change detection has moved to VersionStreamService (pushed via SSE) — this loop
    // now only exists to keep polling session/auth status on the same cadence as before.
    checkForTheNewVersion(delay: number = 60000): void {

        setTimeout(() => {
            this.backendApiService.get('/status')
                .subscribe({
                    next: (res: any) => {
                        if (!res.data.authenticated) {
                            this.showAuthDialog();
                        }
                        this.checkForTheNewVersion(delay);
                    },
                    error: (err) => {
                        // do nothing
                    }
                });
        }, delay);
    }

    goTo(url: string): void {
        if (!url) {
            return;
        }

        window.open(url, '_blank');
    }
}

@Pipe({name: 'truncate'})
export class TruncatePipe implements PipeTransform {
    transform(value: string, limit: number = 32, trail: string = '...'): string {
        return value.length > limit ? value.substring(0, limit) + trail : value;
    }
}
